import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Progress } from 'components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { 
  ArrowLeft, BarChart3, TrendingUp, TrendingDown, Clock, Users, 
  DollarSign, Shield, Star, Calendar as CalendarIcon, Activity, Loader2,
  ArrowUpRight, ArrowDownRight, PieChart, Download, Printer, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function Analytics() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [budgetTrends, setBudgetTrends] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [stressTrends, setStressTrends] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const isFamily = user?.role === 'family_caregiver';

  useEffect(() => {
    fetchData();
  }, [period, isCustomRange, customDateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let requests;
      
      if (isCustomRange && customDateRange.from && customDateRange.to) {
        // Use custom date range endpoint
        const startDate = customDateRange.from.toISOString();
        const endDate = customDateRange.to.toISOString();
        requests = [
          api.get(`/analytics/custom-range?start_date=${startDate}&end_date=${endDate}`),
          api.get('/analytics/performance-summary')
        ];
        
        const responses = await Promise.all(requests);
        
        // Map custom range response to overview format
        const customData = responses[0].data;
        setOverview({
          metrics: customData.metrics,
          date_range: customData.date_range
        });
        setTrends(customData.trends || []);
        setPerformance(responses[1].data);
      } else {
        // Use standard period-based endpoints
        requests = [
          api.get(`/analytics/overview?period=${period}`),
          api.get(`/analytics/trends?period=${period}`),
          api.get('/analytics/performance-summary')
        ];

        if (isFamily) {
          requests.push(
            api.get('/analytics/caregivers'),
            api.get('/analytics/budget-trends')
          );
        }
        
        const responses = await Promise.all(requests);
        
        setOverview(responses[0].data);
        setTrends(responses[1].data.trends || []);
        setPerformance(responses[2].data);
        
        if (isFamily && responses.length > 3) {
          setCaregivers(responses[3].data.caregivers || []);
          setBudgetTrends(responses[4].data);
        }
      }
      
      // Fetch extra insights (non-blocking)
      const extras = [];
      if (isFamily) {
        extras.push(
          api.get('/analytics/caregiver-rankings').catch(() => ({ data: { rankings: [] } }))
        );
      }
      extras.push(
        api.get(`/analytics/stress-trends?period=${period}`).catch(() => ({ data: {} })),
        api.get('/analytics/suggestions').catch(() => ({ data: { suggestions: [] } }))
      );
      const extraRes = await Promise.all(extras);
      
      if (isFamily) {
        setRankings(extraRes[0].data.rankings || []);
        setStressTrends(extraRes[1].data);
        setSuggestions(extraRes[2].data.suggestions || []);
      } else {
        setStressTrends(extraRes[0].data);
        setSuggestions(extraRes[1].data.suggestions || []);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    let url = '/api/analytics/export-report';
    if (isCustomRange && customDateRange.from && customDateRange.to) {
      url += `?start_date=${customDateRange.from.toISOString()}&end_date=${customDateRange.to.toISOString()}`;
    }
    window.open(url, '_blank');
  };

  const handleExportCSV = () => {
    let url = '/api/analytics/export-csv';
    if (isCustomRange && customDateRange.from && customDateRange.to) {
      url += `?start_date=${customDateRange.from.toISOString()}&end_date=${customDateRange.to.toISOString()}`;
    }
    window.open(url, '_blank');
    toast.success('CSV download started');
  };

  const handleExportPDF = () => {
    let url = '/api/analytics/export-pdf';
    if (isCustomRange && customDateRange.from && customDateRange.to) {
      url += `?start_date=${customDateRange.from.toISOString()}&end_date=${customDateRange.to.toISOString()}`;
    }
    window.open(url, '_blank');
    toast.success('PDF download started');
  };

  const handlePeriodChange = (value) => {
    if (value === 'custom') {
      setIsCustomRange(true);
    } else {
      setIsCustomRange(false);
      setPeriod(value);
    }
  };

  const handleDateSelect = (range) => {
    if (range?.from && range?.to) {
      setCustomDateRange({ from: range.from, to: range.to });
    }
  };

  const metrics = overview?.metrics || {};
  const budget = overview?.budget;
  const scores = performance?.scores || {};

  const MetricCard = ({ title, value, change, icon: Icon, color, suffix = '' }) => (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-2xl sm:text-3xl font-bold text-slate-900">{value}{suffix}</p>
            {change !== undefined && (
              <div className={`flex items-center gap-1 text-sm mt-1 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {Math.abs(change)}% vs last period
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Simple bar chart using CSS
  const SimpleChart = ({ data, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d.value), 1);
    return (
      <div className="flex items-end gap-1 h-32">
        {data.slice(-14).map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-teal-500 rounded-t transition-all hover:bg-teal-600"
              style={{ height: `${(d.value / max) * 100}%`, minHeight: d.value > 0 ? '4px' : '0' }}
              title={`${d.label}: ${d.value}`}
            />
            {i % 2 === 0 && (
              <span className="text-xs text-slate-400 mt-1 truncate w-full text-center">
                {d.label}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Analytics</h1>
              <p className="text-xs sm:text-sm text-slate-500">Performance insights and trends</p>
            </div>
          </div>
          <Select value={isCustomRange ? 'custom' : period} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-28 sm:w-40 shrink-0 h-8 sm:h-9 text-xs sm:text-sm" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
          {/* Key Metrics */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${isCustomRange ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
            <MetricCard
              title="Total Hours"
              value={metrics.total_hours || 0}
              change={metrics.hours_change}
              icon={Clock}
              color="bg-teal-500"
              suffix="h"
            />
            <MetricCard
              title="Total Visits"
              value={metrics.total_visits || 0}
              change={metrics.visits_change}
              icon={CalendarIcon}
              color="bg-blue-500"
            />
            <MetricCard
              title="Compliance Rate"
              value={metrics.compliance_rate || 0}
              icon={Shield}
              color="bg-green-500"
              suffix="%"
            />
            <MetricCard
              title="Avg. Shift Duration"
              value={metrics.avg_shift_duration || 0}
              icon={Activity}
              color="bg-purple-500"
              suffix="h"
            />
            
            {/* Custom Date Range Picker Card */}
            {isCustomRange === true && (
              <Card className="flex flex-col justify-center">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-2 font-medium">Custom Date Range</p>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="date-range-btn">
                        <CalendarIcon className="w-4 h-4 text-teal-500" />
                        {customDateRange.from && customDateRange.to ? (
                          <span className="text-sm">
                            {format(customDateRange.from, 'MMM d')} - {format(customDateRange.to, 'MMM d, yyyy')}
                          </span>
                        ) : (
                          <span className="text-slate-500">Select dates</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-50" align="end" sideOffset={5}>
                      <Calendar
                        mode="range"
                        selected={customDateRange}
                        onSelect={handleDateSelect}
                        numberOfMonths={2}
                        disabled={(date) => date > new Date()}
                        className="rounded-md border"
                      />
                    </PopoverContent>
                  </Popover>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Performance Scores */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Scores</CardTitle>
              <CardDescription>Key performance indicators for the past 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6">
                {/* Overall Score */}
                <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl">
                  <div className="text-2xl sm:text-4xl font-bold text-teal-600">{scores.overall_score || 0}%</div>
                  <p className="text-teal-700 font-medium mt-1">Overall Score</p>
                </div>
                
                {/* Individual Scores */}
                <div className="md:col-span-3 space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Approval Rate</span>
                      <span className="font-medium">{scores.approval_rate || 0}%</span>
                    </div>
                    <Progress value={scores.approval_rate || 0} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">EVV Verification Rate</span>
                      <span className="font-medium">{scores.evv_rate || 0}%</span>
                    </div>
                    <Progress value={scores.evv_rate || 0} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Location Verification Rate</span>
                      <span className="font-medium">{scores.location_rate || 0}%</span>
                    </div>
                    <Progress value={scores.location_rate || 0} className="h-2" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trends Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Trends</CardTitle>
              <CardDescription>Hours worked over time</CardDescription>
            </CardHeader>
            <CardContent>
              {trends.length > 0 ? (
                <SimpleChart 
                  data={trends.map(t => ({ 
                    label: t.date.slice(5), 
                    value: t.hours 
                  }))}
                />
              ) : (
                <div className="text-center py-12 text-slate-500">
                  No trend data available for this period
                </div>
              )}
            </CardContent>
          </Card>

          {/* Family-specific: Budget & Caregivers */}
          {isFamily && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Budget Overview */}
              {budget && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-teal-500" />
                      Budget Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Total Budget</span>
                        <span className="text-2xl font-bold">${budget.total?.toLocaleString()}</span>
                      </div>
                      <Progress value={budget.utilization} className="h-3" />
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">
                          Spent: <span className="font-medium text-slate-900">${budget.spent?.toLocaleString()}</span>
                        </span>
                        <span className="text-slate-500">
                          Remaining: <span className="font-medium text-green-600">${budget.remaining?.toLocaleString()}</span>
                        </span>
                      </div>
                      <div className="pt-4 border-t">
                        <p className="text-sm text-slate-500 mb-2">Weekly Spending</p>
                        {budgetTrends?.weekly_trends && (
                          <SimpleChart 
                            data={budgetTrends.weekly_trends.map(w => ({
                              label: w.week,
                              value: w.amount
                            }))}
                          />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Caregiver Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-500" />
                    Caregiver Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {caregivers.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No caregiver data available
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {caregivers.map((cg) => (
                        <div key={cg.user_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold">
                            {cg.name?.[0] || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{cg.name}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span>{cg.total_hours}h worked</span>
                              <span>{cg.total_visits} visits</span>
                              {cg.rating > 0 && (
                                <span className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                                  {cg.rating.toFixed(1)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-teal-600">{cg.compliance_rate}%</div>
                            <div className="text-xs text-slate-500">Compliance</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Shift Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Shift Statistics</CardTitle>
              <CardDescription>Breakdown of shift statuses</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-slate-900">{performance?.shifts?.total || 0}</div>
                  <p className="text-sm text-slate-500">Total Shifts</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{performance?.shifts?.approved || 0}</div>
                  <p className="text-sm text-green-700">Approved</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">{performance?.shifts?.pending || 0}</div>
                  <p className="text-sm text-amber-700">Pending</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{performance?.shifts?.flagged || 0}</div>
                  <p className="text-sm text-red-700">Flagged</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actionable Suggestions */}
          {suggestions.length > 0 && (
            <Card data-testid="suggestions-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-amber-500" />
                  Insights & Suggestions
                </CardTitle>
                <CardDescription>Actionable recommendations based on your data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {suggestions.map((s, i) => (
                  <div
                    key={i}
                    className={`p-4 rounded-lg border flex items-start gap-3 ${
                      s.priority === 'high' ? 'bg-rose-50 border-rose-200' :
                      s.priority === 'medium' ? 'bg-amber-50 border-amber-200' :
                      'bg-blue-50 border-blue-200'
                    }`}
                    data-testid={`suggestion-${s.type}`}
                  >
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      s.priority === 'high' ? 'bg-rose-500' : s.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                    }`} />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{s.title}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{s.message}</p>
                    </div>
                    {s.action_url && (
                      <Button variant="outline" size="sm" onClick={() => navigate(s.action_url)}>View</Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Caregiver Rankings (Family only) */}
          {isFamily && rankings.length > 0 && (
            <Card data-testid="rankings-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400" />
                  Caregiver Rankings
                </CardTitle>
                <CardDescription>Performance ranking over the last 90 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {rankings.map((r, i) => (
                    <div key={r.caregiver_id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`ranking-${i}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        i === 0 ? 'bg-amber-100 text-amber-700' :
                        i === 1 ? 'bg-slate-200 text-slate-700' :
                        i === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        #{i + 1}
                      </div>
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold flex-shrink-0">
                        {r.name?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{r.name}</p>
                        <p className="text-xs text-slate-500">{r.total_shifts} shifts · {r.total_hours}h</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-teal-600">{r.score}</div>
                        <p className="text-xs text-slate-500">Score</p>
                      </div>
                      <div className="hidden sm:grid grid-cols-3 gap-4 text-center text-xs">
                        <div>
                          <p className="font-medium text-green-600">{r.approval_rate}%</p>
                          <p className="text-slate-400">Approval</p>
                        </div>
                        <div>
                          <p className="font-medium text-blue-600">{r.evv_rate}%</p>
                          <p className="text-slate-400">EVV</p>
                        </div>
                        <div>
                          <p className="font-medium text-purple-600">{r.punctuality}%</p>
                          <p className="text-slate-400">On-Time</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stress/Burnout Trends */}
          {stressTrends?.summary && (
            <Card data-testid="stress-trends-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-rose-500" />
                  Stress & Burnout Trends
                </CardTitle>
                <CardDescription>Wellness patterns from your check-ins</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <div className="text-xl font-bold">{stressTrends.summary.total_checkins}</div>
                    <p className="text-xs text-slate-500">Check-ins</p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${stressTrends.summary.avg_stress >= 4 ? 'bg-rose-50' : stressTrends.summary.avg_stress >= 3 ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <div className={`text-xl font-bold ${stressTrends.summary.avg_stress >= 4 ? 'text-rose-600' : stressTrends.summary.avg_stress >= 3 ? 'text-amber-600' : 'text-green-600'}`}>
                      {stressTrends.summary.avg_stress}/5
                    </div>
                    <p className="text-xs text-slate-500">Avg Stress</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-rose-600">{stressTrends.summary.high_stress_days}</div>
                    <p className="text-xs text-slate-500">High-Stress Days</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <div className={`text-xl font-bold ${stressTrends.summary.trend_direction === 'improving' ? 'text-green-600' : stressTrends.summary.trend_direction === 'worsening' ? 'text-rose-600' : 'text-slate-600'}`}>
                      {stressTrends.summary.trend_direction === 'improving' ? '↓' : stressTrends.summary.trend_direction === 'worsening' ? '↑' : '→'}
                    </div>
                    <p className="text-xs text-slate-500 capitalize">{stressTrends.summary.trend_direction}</p>
                  </div>
                </div>
                {stressTrends.trends?.length > 0 && (
                  <div className="h-32 flex items-end gap-1">
                    {stressTrends.trends.slice(-30).map((t, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div
                          className={`w-full rounded-t ${t.avg_stress >= 4 ? 'bg-rose-400' : t.avg_stress >= 3 ? 'bg-amber-400' : 'bg-green-400'}`}
                          style={{ height: `${(t.avg_stress / 5) * 100}%` }}
                          title={`${t.date}: ${t.avg_stress}/5`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
